import React from "react";

import { Container, Row, Col, Table, Dropdown, DropdownButton, ButtonGroup } from "react-bootstrap";
import { FormGroup, Label, Input } from "reactstrap";
import RealtimeConnection from "../../utils/RealtimeConnection";
import InputNumber from "rc-input-number";

import Translate from "../../utils/Translate";

import "../../assets/css/Forms.css";
const subTextsFirst = {
  "($5 / $5)": "(Y1 : $5)",
  "($5 / $5 / $5 / $5)": "(Y1 : $5 / Y2 : $5)",
  "($2 / $2)": "(Y1 : $4)",
  "($2 / $2 / $2)": "(Y1 : $4 / Y2 : $2)",
};
const subTextSecond = {
  0: { P: "P ($10)", E: "E ($5)" },
  1: { P: "P ($5)", E: "E ($5)" },
  2: { P: "P ($2)", E: "E ($5)" },
};

export default function StrategyPaper({
  canEdit,
  dispatch,
  strategyPaper,
  session,
  isSessionLong,
}) {
  function closeStrategy(e) {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    dispatch({ type: "strategyPaper", value: false });
  }

  function handleChange(
    alias,
    companyIndex,
    value,
    categoryIndex,
    subCategoryIndex,
    fieldIndex,
    liquidityIndex
  ) {
    RealtimeConnection.changeStrategyPaper({
      year: session.formSelectedYear,
      alias,
      companyIndex,
      value,
      categoryIndex,
      subCategoryIndex,
      fieldIndex,
      liquidityIndex,
    });
  }

  function handleCreditChange(field, value) {
    RealtimeConnection.changeStrategyPaper({
      year: session.formSelectedYear,
      alias: "credits",
      field,
      value,
    });
  }

  function changeFormYear(value) {
    RealtimeConnection.changeFormYear({
      year: value,
      type: "strategyPaper",
    });
  }

  let yearsOptions = [];
  for (let i = 0; i <= session.year; i++) {
    yearsOptions.push(
      <Dropdown.Item key={i} eventKey={i} onClick={() => changeFormYear(i)}>
        Year {i}
      </Dropdown.Item>
    );
  }

  let formStatus = canEdit ? true : false;
  if (session.formSelectedYear !== session.year) {
    formStatus = false;
  }
// helper (numeric only, allow + / -, arrows, backspace)
const numericOnly = (e) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "Tab",
    "Home",
    "End",
    "-",
    "+",
    "ArrowUp",
    "ArrowDown",
  ];

  if (
    allowedKeys.includes(e.key) ||
    (e.key >= "0" && e.key <= "9")
  ) {
    return;
  }

  e.preventDefault();
};

  function computeTotalLiquidity(paper) {
    let total = 0;
    if (!paper) return total;

    // First section
    (paper.first || []).forEach(c => {
      total += Number(c.Liquidity) || 0;
    });

    // Second section
    (paper.second || []).forEach(c => {
      total += Number(c.Liquidity) || 0;
    });

    // Third section
    (paper.third || []).forEach(c => {
      (c.Liquidity || []).forEach(l => {
        total += Number(l.value) || 0;
      });
    });

    // Fourth section (sum everything except the "Total Liquidity needs" line)
    (paper.fourth?.Liquidity || []).forEach(l => {
      if (l && l.name !== "Total Liquidity needs (before revenues)") {
        total += Number(l.value) || 0;
      }
    });

    return total;
  }

  const computedTotal = computeTotalLiquidity(strategyPaper);

  return (
    <Container className="formContainer">
      <Row>
        <Col>
          <div className="formHeader">
            <div className="headerTitle bold">
              <Translate alias="Forms" word="Strategy Paper" />
            </div>
            <ButtonGroup style={{ marginLeft: "10px" }}>
              <button 
                className="btn btn-outline-secondary btn-sm"
                disabled={session.formSelectedYear <= 0}
                onClick={() => changeFormYear(session.formSelectedYear - 1)}
              >
                &lt;
              </button>
              <DropdownButton
                as={ButtonGroup}
                variant="outline-secondary"
                title={`Year ${session.formSelectedYear}`}
                size="sm"
              >
                {yearsOptions}
              </DropdownButton>
              <button 
                className="btn btn-outline-secondary btn-sm"
                disabled={session.formSelectedYear >= session.year}
                onClick={() => changeFormYear(session.formSelectedYear + 1)}
              >
                &gt;
              </button>
            </ButtonGroup>
            <div className="closeForm" onClick={closeStrategy}>
              X
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Table bordered>
            <tbody>
              {strategyPaper.first.map((company, companyIndex) => (
                <tr key={`first-company-${companyIndex}`}>
                  <td style={{ width: "10px" }}>
                    <div className="bold">
                      <Translate alias="Forms" word={company.name} />
                    </div>
                  </td>
                  <td>
                    {(company.categories || []).map((category, categoryIndex) => {
                      const subText = isSessionLong
                        ? category.subText
                        : subTextsFirst[category.subText];
                      return (
                        <React.Fragment key={`first-${companyIndex}-cat-${categoryIndex}`}>
                          <tr>
                            <td>
                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    disabled={!canEdit}
                                    checked={category.value}
                                    onChange={(e) =>
                                      handleChange(
                                        "first",
                                        companyIndex,
                                        e.target.checked,
                                        categoryIndex
                                      )
                                    }
                                  />{" "}
                                  <div className="bold">
                                    <Translate alias="Forms" word={category.name.trim()} />
                                  </div>
                                  <div className="subtext">
                                    {subText}
                                  </div>
                                </Label>
                              </FormGroup>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </td>
                  <td>
                    <InputNumber
                      min={0}
                      className="liquidity largeField"
                      value={company.Liquidity}
                      onChange={(value) => handleChange("first", companyIndex, value)}
                      disabled={!canEdit}
                      formatter={(value) => `${value}`.replace(/\$/g, "")}
                      parser={(value) => value.replace(/\$/g, "")}
                    />
                  </td>
                </tr>
              ))}

              {strategyPaper.second.map((company, companyIndex) => (
                <tr key={`second-company-${companyIndex}`}>
                  <td>
                    <div className="bold">
                      {
                        isSessionLong ?
                          company.name
                          :
                          <Translate alias="Forms" word="Purchase/Lease- Property (P) Purchase- Equipment (E) Service (S) *Rent only for Service Hub Property" />
                      }
                    </div>
                  </td>
                  <td>
           {/* // In StrategyPaper.js - Update the rendering for the second section */}
{(company.categories || []).map((category, categoryIndex) => (
  <React.Fragment key={`second-${companyIndex}-cat-${categoryIndex}`}>
    <tr>
      <td>
        <div className="bold border-bottom">
          <Translate alias="Forms" word={category.name.trim()} />
        </div>
        <div className="d-flex justify-content-between">
          {category.subCategories.map((subCategory, subCategoryIndex) => (
            <td key={`second-${companyIndex}-cat-${categoryIndex}-sub-${subCategoryIndex}`}>
              <div className="bold border-bottom text-center">
                <Translate alias="Forms" word={subCategory.name} />
              </div>
              {(subCategory.fields || []).map((field, fieldIndex) => {
                const name = isSessionLong
                  ? field.name
                  : categoryIndex === 3
                    ? subCategory.name === "Low Cost"
                      ? subTextSecond[2][field.name]
                      : subTextSecond[1][field.name]
                    : subTextSecond[0][field.name];

                return (
                  <div
                    key={`second-${companyIndex}-${categoryIndex}-${subCategoryIndex}-field-${fieldIndex}`}
                    className="d-flex flex-column align-items-center p-1 border rounded"
                    style={{ minWidth: "80px" }}
                  >
                    {/* Field Name */}
                    <div className="bold mb-1" style={{ fontSize: "0.9em" }}>
                      {name}
                    </div>

                    {/* Buy/Sell/None Selector */}
                    <Input
                      type="select"
                      disabled={!canEdit}
                      value={field.action || "none"}
                      onChange={(e) =>
                        handleChange(
                          "second",
                          companyIndex,
                          { ...field, action: e.target.value },
                          categoryIndex,
                          subCategoryIndex,
                          fieldIndex
                        )
                      }
                      className="mb-1"
                      style={{ fontSize: "0.8em", padding: "2px" }}
                    >
                      <option value="none">None</option>
                      {categoryIndex === 3 && field.name === "P" ? (
                         <option value="rent">Rent</option>
                      ) : (
                        <>
                          <option value="buy">Buy</option>
                          <option value="sell">Sell</option>
                        </>
                      )}
                    </Input>

                    {/* Amount Input */}
<div className="d-flex align-items-center" style={{ minWidth: "80px" }}>
  <InputNumber
    className="liquidity smallField"
    value={field.amount || 0}
    formatter={(value) => `${value}`.replace(/\$/g, "")}
    parser={(value) => value.replace(/\$/g, "")}
    onChange={(val) =>
      handleChange(
        "second",
        companyIndex,
        { ...field, amount: val },
        categoryIndex,
        subCategoryIndex,
        fieldIndex
      )
    }
      min={-999}
  max={999}
    disabled={!canEdit}
    style={{ width: "60px" }}
    // Allow negative values for selling
  />

</div>
                  </div>
                );
              })}
            </td>
          ))}
        </div>
      </td>
    </tr>
  </React.Fragment>
))}
                  </td>
                  <td>
                    <InputNumber
                      min={0}
                      className="liquidity largeField"
                      value={company.Liquidity}
                      onChange={(value) => handleChange("second", companyIndex, value)}
                      disabled={!canEdit}
                      formatter={(value) => `${value}`.replace(/\$/g, "")}
                      parser={(value) => value.replace(/\$/g, "")}
                    />
                  </td>
                </tr>
              ))}

              {strategyPaper.third.map((company, companyIndex) => (
                <tr key={`third-company-${companyIndex}`}>
                  <td>
                    <div className="bold">
                      <Translate alias="Forms" word={company.name} />
                    </div>
                  </td>
                  <td>
                    {(company.categories || []).map((category, categoryIndex) => (
                      <React.Fragment key={`third-${companyIndex}-cat-${categoryIndex}`}>
                        <tr>
                          <td>
                            <div className="bold border-bottom">
                              <Translate alias="Forms" word={category.name.trim()} />
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                              {(category.subCategories || []).map((subCategory, subCategoryIndex) => (
                                <td key={`third-${companyIndex}-cat-${categoryIndex}-sub-${subCategoryIndex}`} style={{ position: "relative" }}>
                                  <div className="bold border-bottom text-center">
                                    {subCategory.name}
                                  </div>
                                  {subCategoryIndex === 0 && (
                                    <div className="plus-minus">
                                      <span>+ / -</span>
                                      <span><Translate alias="Forms" word="New Total" /></span>{" "}
                                    </div>
                                  )}
                                  {(subCategory.fields || []).map((field, fieldIndex) => (
                                    <React.Fragment key={`third-${companyIndex}-${categoryIndex}-${subCategoryIndex}-field-${fieldIndex}`}>
                                      <InputNumber
                                        min={-9999}
                                        className="liquidity"
                                        value={field.value}
                                        formatter={(value) => `${value}`.replace(/\$/g, "")}
                                        parser={(value) => value.replace(/\$/g, "")}
                                        onChange={(value) =>
                                          handleChange(
                                            "third",
                                            companyIndex,
                                            value,
                                            categoryIndex,
                                            subCategoryIndex,
                                            fieldIndex
                                          )
                                        }
                                        disabled={!canEdit}
                                      />
                                      <div className="bold border-bottom text-center">
                                        {field.name}
                                      </div>
                                    </React.Fragment>
                                  ))}
                                </td>
                              ))}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </td>
                  <td className="td-liquidity">
                    <div className="d-flex flex-column">
                      {(company.Liquidity || []).map((item, liquidityIndex) => (
                        <React.Fragment key={`third-${companyIndex}-liq-${liquidityIndex}`}>
                          <InputNumber
                            min={0}
                            className="liquidity largeField"
                            value={item.value}
                            formatter={(value) => `${value}`.replace(/\$/g, "")}
                            parser={(value) => value.replace(/\$/g, "")}
                            onChange={(value) =>
                              handleChange(
                                "third",
                                companyIndex,
                                value,
                                undefined,
                                undefined,
                                undefined,
                                liquidityIndex
                              )
                            }
                            disabled={!canEdit}
                          />

                          <div
                            className="bold text-center"
                            style={
                              company.Liquidity.length - 1 !== liquidityIndex
                                ? { borderBottom: "1px solid white" }
                                : null
                            }
                          >
                            <Translate alias="Forms" word={item.name} />
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}

              <tr key="fourth-row">
                <td>
                  <div className="bold">
                    <Translate alias="Forms" word={strategyPaper.fourth.name} />
                  </div>
                </td>
                <td style={{ verticalAlign: "top" }}>
                  {/* Credit Take-up Section – SIMFlex style, merged into the main row */}
                  {/* Credit Take-up Section – Refactored Layout */}
                  <div className="d-flex justify-content-between align-items-start w-100">
                      
                      {/* Group 1: Outstanding Amount */}
                      <div className="d-flex flex-column align-items-center">
                          <div className="bold mb-2" style={{ minHeight: '1.5em' }}>
                              <Translate alias="Forms" word="Outstanding Amount" />
                          </div>
                          <div className="d-flex">
                              <div className="text-center" style={{ marginRight: '10px' }}>
                                  <InputNumber
                                    min={0}
                                    className="liquidity largeField"
                                    value={strategyPaper.credits?.fivePercent || 0}
                                    onKeyDown={numericOnly}
                                    onChange={(v) => handleCreditChange("fivePercent", v)}
                                    disabled={!canEdit}
                                    formatter={(value) => `${value}`.replace(/\$/g, "")}
                                    parser={(value) => value.replace(/\$/g, "")}
                                  />
                                  <div className="bold">5%</div>
                              </div>
                              <div className="text-center">
                                  <InputNumber
                                    min={0}
                                    className="liquidity largeField"
                                    value={strategyPaper.credits?.tenPercent || 0}
                                    onKeyDown={numericOnly}
                                    onChange={(v) => handleCreditChange("tenPercent", v)}
                                    disabled={!canEdit}
                                    formatter={(value) => `${value}`.replace(/\$/g, "")}
                                    parser={(value) => value.replace(/\$/g, "")}
                                  />
                                  <div className="bold">10%</div>
                              </div>
                          </div>
                      </div>

                      {/* Group 2: Draw / Repay */}
                      <div className="d-flex flex-column align-items-center">
                           <div className="bold mb-2" style={{ minHeight: '1.5em' }}>
                               <Translate alias="Forms" word="Draw / Repay" />
                           </div>
                           <InputNumber
                             className="liquidity largeField"
                             value={strategyPaper.credits?.drewRepay || 0}
                             onKeyDown={numericOnly}
                             onChange={(v) => handleCreditChange("drewRepay", v)}
                             disabled={!canEdit}
                             min={-9999999}
                             formatter={(value) => `${value}`.replace(/\$/g, "")}
                             parser={(value) => value.replace(/\$/g, "")}
                           />
                            <div className="bold">+/-</div>
                      </div>

                      {/* Group 3: New Credit Status */}
                      <div className="d-flex flex-column align-items-center">
                          {/* Spacer to match header height */}
                          <div className="bold mb-2" style={{ minHeight: '1.5em', visibility: 'hidden' }}>
                              Spacer
                          </div>
                          <InputNumber
                            className="liquidity largeField"
                            value={
                              (Number(strategyPaper.credits?.fivePercent) || 0) +
                              (Number(strategyPaper.credits?.tenPercent) || 0) +
                              (Number(strategyPaper.credits?.drewRepay) || 0)
                            }
                            disabled
                            formatter={(value) => `${value}`.replace(/\$/g, "")}
                            parser={(value) => value.replace(/\$/g, "")}
                          />
                         <div className="bold">
                             <Translate alias="Forms" word="New credit status" />
                         </div>
                      </div>

                  </div>
                </td>

                <td className="td-liquidity">
                  <div className="d-flex flex-column">
                    {(strategyPaper.fourth.Liquidity || []).map((item, liquidityIndex) => {
                      const isTotal = item.name === "Total Liquidity needs (before revenues)";
                      const val = isTotal ? computedTotal : item.value;
                      return (
                        <React.Fragment key={`fourth-liq-${liquidityIndex}`}>
                          <InputNumber
                            min={0}
                            className="liquidity largeField"
                            value={val}
                            formatter={(value) => `${value}`.replace(/\$/g, "")}
                            parser={(value) => value.replace(/\$/g, "")}
                            onChange={(value) =>
                              // if it's computed total, ignore edits (read-only)
                              isTotal
                                ? null
                                : handleChange(
                                    "fourth",
                                    undefined,
                                    value,
                                    undefined,
                                    undefined,
                                    undefined,
                                    liquidityIndex
                                  )
                            }
                            disabled={isTotal || !canEdit}
                          />

                          <div
                            className="bold text-center"
                            style={
                              strategyPaper.fourth.Liquidity.length - 1 !== liquidityIndex
                                ? { borderBottom: "1px solid white" }
                                : null
                            }
                          >
                            <Translate alias="Forms" word={item.name} />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </td>
              </tr>
{/* Credit Take-up Section – SIMFlex style */}



            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}